import Radom, {
  CheckoutFunnelRail,
  CheckoutFunnelSourceType,
  CheckoutFunnelStep
} from '../api/Radom'

const SESSION_KEY_PREFIX = 'radom:checkout-funnel-session'
const PERSISTED_SESSION_KEY_TTL_MS = 6 * 60 * 60 * 1000

interface StoredSessionKey {
  sessionKey: string
  createdAt: number
}

const generateSessionKey = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

const getClientMetadata = (): Record<string, unknown> => {
  if (typeof window === 'undefined') {
    return {}
  }

  const viewportWidth = window.innerWidth
  const viewportHeight = window.innerHeight
  const isTouch = window.matchMedia('(pointer: coarse)').matches

  let referrerHost: string | undefined
  if (document.referrer) {
    try {
      referrerHost = new URL(document.referrer).hostname
    } catch {
      referrerHost = undefined
    }
  }

  const deviceType =
    viewportWidth <= 768
      ? 'mobile'
      : viewportWidth <= 1024
        ? 'tablet'
        : 'desktop'

  return {
    checkoutClient: 'pay-web',
    pagePath: window.location.pathname,
    referrerHost,
    viewportWidth,
    viewportHeight,
    deviceType,
    inputMode: isTouch ? 'touch' : 'mouse',
    locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
  }
}

export const getCheckoutFunnelSessionKey = (
  sourceType: CheckoutFunnelSourceType,
  sourceId: string
): string => {
  if (typeof window === 'undefined') {
    return `${sourceType}-${sourceId}`
  }

  const storageKey = `${SESSION_KEY_PREFIX}:${sourceType}:${sourceId}`
  const existing =
    getStoredSessionKey(window.sessionStorage, storageKey) ??
    getStoredSessionKey(window.localStorage, storageKey, PERSISTED_SESSION_KEY_TTL_MS)

  if (existing) {
    persistSessionKey(window.sessionStorage, storageKey, existing)
    persistSessionKey(window.localStorage, storageKey, existing, true)
    return existing
  }

  const sessionKey = `${sourceType}-${sourceId}-${generateSessionKey()}`
  persistSessionKey(window.sessionStorage, storageKey, sessionKey)
  persistSessionKey(window.localStorage, storageKey, sessionKey, true)
  return sessionKey
}

const parseStoredSessionKey = (
  raw: string,
  ttlMs?: number
): string | undefined => {
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSessionKey>
    if (typeof parsed.sessionKey !== 'string' || parsed.sessionKey.length === 0) {
      return undefined
    }

    if (ttlMs !== undefined) {
      const createdAt = typeof parsed.createdAt === 'number' ? parsed.createdAt : 0
      if (Date.now() - createdAt > ttlMs) {
        return undefined
      }
    }

    return parsed.sessionKey
  } catch {
    return raw.length > 0 ? raw : undefined
  }
}

const getStoredSessionKey = (
  storage: Storage,
  storageKey: string,
  ttlMs?: number
): string | undefined => {
  try {
    const raw = storage.getItem(storageKey)
    if (!raw) {
      return undefined
    }

    return parseStoredSessionKey(raw, ttlMs)
  } catch {
    return undefined
  }
}

const persistSessionKey = (
  storage: Storage,
  storageKey: string,
  sessionKey: string,
  includeTimestamp = false
): void => {
  try {
    storage.setItem(
      storageKey,
      includeTimestamp
        ? JSON.stringify({ sessionKey, createdAt: Date.now() } satisfies StoredSessionKey)
        : sessionKey
    )
  } catch {
  }
}

export const trackCheckoutFunnelStep = async ({
  sourceType,
  sourceId,
  rail,
  step,
  metadata
}: {
  sourceType: CheckoutFunnelSourceType
  sourceId: string
  rail: CheckoutFunnelRail
  step: CheckoutFunnelStep
  metadata?: Record<string, unknown>
}): Promise<void> => {
  const trackEvent = (Radom as any)?.trackCheckoutFunnelEvent
  if (typeof trackEvent !== 'function') {
    return
  }

  try {
    await trackEvent.call(Radom, {
      sourceType,
      sourceId,
      rail,
      step,
      sessionKey: getCheckoutFunnelSessionKey(sourceType, sourceId),
      metadata: {
        ...getClientMetadata(),
        ...metadata
      }
    })
  } catch (err) {
    console.warn('Failed to track checkout funnel step', err)
  }
}
